const DEFAULT_FACE_GEOMETRY_ANCHORS = [
  10, 152, 1, 2, 4, 6, 9, 33, 133, 263, 362, 61, 291, 78, 308, 234, 454
]

const EPS = 1e-8

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

const normalizeQuat = (q) => {
  const n = Math.hypot(q[0], q[1], q[2], q[3]) || 1
  return [q[0] / n, q[1] / n, q[2] / n, q[3] / n]
}

const quatToMat3 = (q) => {
  const [w, x, y, z] = q
  const xx = x * x
  const yy = y * y
  const zz = z * z
  const xy = x * y
  const xz = x * z
  const yz = y * z
  const wx = w * x
  const wy = w * y
  const wz = w * z
  return [
    1 - 2 * (yy + zz),
    2 * (xy - wz),
    2 * (xz + wy),
    2 * (xy + wz),
    1 - 2 * (xx + zz),
    2 * (yz - wx),
    2 * (xz - wy),
    2 * (yz + wx),
    1 - 2 * (xx + yy)
  ]
}

const mat3MulVec = (m, v) => ({
  x: m[0] * v.x + m[1] * v.y + m[2] * v.z,
  y: m[3] * v.x + m[4] * v.y + m[5] * v.z,
  z: m[6] * v.x + m[7] * v.y + m[8] * v.z
})

const estimateQuaternionFromCovariance = (h) => {
  const hxx = h[0]
  const hxy = h[1]
  const hxz = h[2]
  const hyx = h[3]
  const hyy = h[4]
  const hyz = h[5]
  const hzx = h[6]
  const hzy = h[7]
  const hzz = h[8]

  // Horn's quaternion matrix from covariance.
  const n = [
    hxx + hyy + hzz,
    hyz - hzy,
    hzx - hxz,
    hxy - hyx,

    hyz - hzy,
    hxx - hyy - hzz,
    hxy + hyx,
    hzx + hxz,

    hzx - hxz,
    hxy + hyx,
    -hxx + hyy - hzz,
    hyz + hzy,

    hxy - hyx,
    hzx + hxz,
    hyz + hzy,
    -hxx - hyy + hzz
  ]

  let q = [1, 0, 0, 0]
  for (let iter = 0; iter < 16; iter += 1) {
    const nq = [
      n[0] * q[0] + n[1] * q[1] + n[2] * q[2] + n[3] * q[3],
      n[4] * q[0] + n[5] * q[1] + n[6] * q[2] + n[7] * q[3],
      n[8] * q[0] + n[9] * q[1] + n[10] * q[2] + n[11] * q[3],
      n[12] * q[0] + n[13] * q[1] + n[14] * q[2] + n[15] * q[3]
    ]
    q = normalizeQuat(nq)
  }
  return q
}

const buildPoseMatrix = (r, t, s) => [
  s * r[0], s * r[1], s * r[2], t[0],
  s * r[3], s * r[4], s * r[5], t[1],
  s * r[6], s * r[7], s * r[8], t[2],
  0, 0, 0, 1
]

class FaceGeometryEngine {
  constructor() {
    this.defaultAnchors = DEFAULT_FACE_GEOMETRY_ANCHORS
  }

  estimateSimilarityTransform(sourcePoints, targetPoints, anchorIndices = this.defaultAnchors) {
    if (!sourcePoints?.length || !targetPoints?.length) {
      return { ok: false, reason: 'missing-points' }
    }

    const pairs = []
    for (let i = 0; i < anchorIndices.length; i += 1) {
      const idx = anchorIndices[i]
      const s = sourcePoints[idx]
      const t = targetPoints[idx]
      if (!s || !t) continue
      const sx = Number(s.x)
      const sy = Number(s.y)
      const sz = Number(s.z || 0)
      const tx = Number(t.x)
      const ty = Number(t.y)
      const tz = Number(t.z || 0)
      if (
        !Number.isFinite(sx) ||
        !Number.isFinite(sy) ||
        !Number.isFinite(sz) ||
        !Number.isFinite(tx) ||
        !Number.isFinite(ty) ||
        !Number.isFinite(tz)
      ) {
        continue
      }
      pairs.push({
        s: { x: sx, y: sy, z: sz },
        t: { x: tx, y: ty, z: tz }
      })
    }

    if (pairs.length < 4) return { ok: false, reason: 'insufficient-pairs' }

    const meanS = { x: 0, y: 0, z: 0 }
    const meanT = { x: 0, y: 0, z: 0 }
    for (let i = 0; i < pairs.length; i += 1) {
      const p = pairs[i]
      meanS.x += p.s.x
      meanS.y += p.s.y
      meanS.z += p.s.z
      meanT.x += p.t.x
      meanT.y += p.t.y
      meanT.z += p.t.z
    }
    meanS.x /= pairs.length
    meanS.y /= pairs.length
    meanS.z /= pairs.length
    meanT.x /= pairs.length
    meanT.y /= pairs.length
    meanT.z /= pairs.length

    // Covariance H = X^T Y.
    const h = new Float64Array(9)
    let sourceEnergy = 0
    for (let i = 0; i < pairs.length; i += 1) {
      const sx = pairs[i].s.x - meanS.x
      const sy = pairs[i].s.y - meanS.y
      const sz = pairs[i].s.z - meanS.z
      const tx = pairs[i].t.x - meanT.x
      const ty = pairs[i].t.y - meanT.y
      const tz = pairs[i].t.z - meanT.z

      h[0] += sx * tx
      h[1] += sx * ty
      h[2] += sx * tz
      h[3] += sy * tx
      h[4] += sy * ty
      h[5] += sy * tz
      h[6] += sz * tx
      h[7] += sz * ty
      h[8] += sz * tz
      sourceEnergy += sx * sx + sy * sy + sz * sz
    }

    if (sourceEnergy <= EPS) return { ok: false, reason: 'degenerate-source' }

    const q = estimateQuaternionFromCovariance(h)
    const r = quatToMat3(q)

    let numerator = 0
    for (let i = 0; i < pairs.length; i += 1) {
      const sx = pairs[i].s.x - meanS.x
      const sy = pairs[i].s.y - meanS.y
      const sz = pairs[i].s.z - meanS.z
      const tx = pairs[i].t.x - meanT.x
      const ty = pairs[i].t.y - meanT.y
      const tz = pairs[i].t.z - meanT.z
      const rsx = r[0] * sx + r[1] * sy + r[2] * sz
      const rsy = r[3] * sx + r[4] * sy + r[5] * sz
      const rsz = r[6] * sx + r[7] * sy + r[8] * sz
      numerator += rsx * tx + rsy * ty + rsz * tz
    }
    const scale = clamp(numerator / sourceEnergy, 0.5, 1.9)

    const rMeanS = mat3MulVec(r, meanS)
    const t = [
      meanT.x - scale * rMeanS.x,
      meanT.y - scale * rMeanS.y,
      meanT.z - scale * rMeanS.z
    ]

    let err = 0
    for (let i = 0; i < pairs.length; i += 1) {
      const rp = mat3MulVec(r, pairs[i].s)
      const px = scale * rp.x + t[0]
      const py = scale * rp.y + t[1]
      const pz = scale * rp.z + t[2]
      const dx = px - pairs[i].t.x
      const dy = py - pairs[i].t.y
      const dz = pz - pairs[i].t.z
      err += dx * dx + dy * dy + dz * dz
    }

    return {
      ok: true,
      anchorCount: pairs.length,
      rotation: r,
      translation: t,
      scale,
      rmse: Math.sqrt(err / pairs.length),
      matrix: buildPoseMatrix(r, t, scale)
    }
  }

  transformPoints(points, transform, out = null) {
    if (!points?.length || !transform?.ok) return null
    const r = transform.rotation
    const t = transform.translation
    const s = transform.scale
    const dest =
      out && out.length === points.length
        ? out
        : Array.from({ length: points.length }, () => ({ x: 0, y: 0, z: 0 }))
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i]
      if (!p) continue
      const x = Number(p.x) || 0
      const y = Number(p.y) || 0
      const z = Number(p.z) || 0
      dest[i].x = s * (r[0] * x + r[1] * y + r[2] * z) + t[0]
      dest[i].y = s * (r[3] * x + r[4] * y + r[5] * z) + t[1]
      dest[i].z = s * (r[6] * x + r[7] * y + r[8] * z) + t[2]
    }
    return dest
  }

  estimateFaceGeometry({ canonicalMesh, runtimeMesh, triangles, anchors = this.defaultAnchors }) {
    const pose = this.estimateSimilarityTransform(canonicalMesh, runtimeMesh, anchors)
    if (!pose.ok) return { ok: false, pose }
    const mesh = this.transformPoints(canonicalMesh, pose)
    return {
      ok: true,
      poseMatrix: pose.matrix,
      transform: pose,
      mesh,
      triangles: triangles || []
    }
  }
}

export const faceGeometryEngine = new FaceGeometryEngine()
export { DEFAULT_FACE_GEOMETRY_ANCHORS }
export default faceGeometryEngine

